import { Link } from 'react-router-dom';

/**
 * Lower band per PRD §5.3 — 4×4 grid, 16 tiles: 14 §6 tabs + FAQ +
 * Pricing. Investors + legal links live in the footer.
 *
 * The Pricing tile is visually distinguished (sovereign-purple bg + white
 * text) because it is the primary CTA on the home page even though pilot
 * customers route through Sales per §8.
 *
 * On desktop: 4 columns × 4 rows. Tablet: 2 columns. Mobile: 1 column.
 */

interface Tile {
  slug: string;
  title: string;
  teaser: string;
  glyph: string;
  emphasise?: boolean;
}

const TILES: Tile[] = [
  { slug: 'cockpit',              title: 'The Cockpit',              teaser: 'Missions, decisions, drafts, meetings — one shell, live source health.',            glyph: '◧' },
  { slug: 'sovereign-proxy',      title: 'The Sovereign Proxy',      teaser: 'The single chokepoint every AI call passes through, redacted at egress.',           glyph: '⛨' },
  { slug: 'hierarchy',            title: 'Hierarchy Awareness',      teaser: 'Signature rules follow the reports-to graph. Cascade is automatic.',                glyph: '⚑' },
  { slug: 'targets',              title: 'My Targets & KPIs',        teaser: 'Live RAG bands. Pre-threshold drift signals before the band trips.',                glyph: '◉' },
  { slug: 'decisions',            title: 'Decision Management',      teaser: 'Signed, ledger-anchored decisions. Step-up MFA at commit. Do-It engine.',          glyph: '⚖' },
  { slug: 'synapse',              title: 'The Synapse',              teaser: 'Meetings + calls captured, sealed, summarised — with human confirmation.',          glyph: '◎' },
  { slug: 'ai-gateway',           title: 'The AI Gateway',           teaser: 'Route between frontier + tenant-VPC + on-prem models per sensitivity.',             glyph: '↯' },
  { slug: 'genwork', title: 'GenWork',     teaser: 'AI-drafted output that respects your sensitivity boundary. Four tiers.',           glyph: '✎' },
  { slug: 'what-if',              title: 'What-If Simulations',      teaser: 'Replay decisions under alternate policies. Reproducible from the ledger.',          glyph: '⟳' },
  { slug: 'insights',             title: 'Priority Insights',        teaser: 'The single-glance answer to "what needs my attention right now".',                  glyph: '★' },
  { slug: 'developers',           title: 'Developers Access',        teaser: 'OpenAI + Anthropic proxies for IDE plugins. Personal API tokens.',                 glyph: '⌘' },
  { slug: 'ledger',               title: 'The Immutable Ledger',     teaser: 'Hash-chained, signed, exportable, regulator-verifiable.',                          glyph: '☰' },
  { slug: 'ml-layer',             title: 'The Sovereign Machine Learning Layer',   teaser: 'Learn from your own decisions — inside your boundary. Five opt-in stages.',        glyph: '◈' },
  { slug: 'eu-ai-act',            title: 'EU AI Act',                teaser: 'Ready for the Act, article by article. DPIA + conformity file included.',          glyph: '§' },
  { slug: 'faq',                  title: 'FAQ',                      teaser: 'The twenty questions that come up first in every pilot conversation.',              glyph: '?' },
  { slug: 'pricing',              title: 'Pricing',                  teaser: 'Cloud + on-premise service tiers. Tailored pilots for first customers.',           glyph: '€', emphasise: true },
];

export function TabGrid(): JSX.Element {
  return (
    <section
      aria-label="Explore DI Deck"
      className="bg-sovereign-900 px-3 py-4 md:px-8 md:py-5"
    >
      <div className="mx-auto grid w-full max-w-6xl grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 md:gap-2.5">
        {TILES.map((t) => (
          <TabTile key={t.slug} tile={t} />
        ))}
      </div>
    </section>
  );
}

function TabTile({ tile }: { tile: Tile }): JSX.Element {
  const emphasise = tile.emphasise === true;
  return (
    <Link
      to={`/${tile.slug}`}
      className={
        emphasise
          ? 'group flex flex-col rounded-md bg-brand-purple p-2 shadow-card transition-all hover:shadow-card-hover'
          : 'group flex flex-col rounded-md border border-sovereign-line bg-white p-2 shadow-card transition-all hover:border-brand-purple hover:shadow-card-hover'
      }
    >
      <div className="mb-1 flex items-center gap-1.5">
        <span
          className={emphasise ? 'text-sm text-white/90' : 'text-sm text-brand-purple'}
          aria-hidden
        >
          {tile.glyph}
        </span>
        <span
          className={
            emphasise
              ? 'text-[11px] font-bold text-white truncate'
              : 'text-[11px] font-bold text-ink truncate'
          }
        >
          {tile.title}
        </span>
      </div>
      <p
        className={
          emphasise
            ? 'text-[10px] leading-snug text-white/85 line-clamp-2'
            : 'text-[10px] leading-snug text-ink-mute line-clamp-2'
        }
      >
        {tile.teaser}
      </p>
    </Link>
  );
}
